import { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import type { InvestmentProperty, MapBounds, PropertyCluster } from '../../types';
import 'leaflet/dist/leaflet.css';
import './PropertyMap.css';

// Fix Leaflet marker icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Format price for marker label (Zillow-style)
const formatPriceLabel = (price: number): string => {
  if (price >= 1000000) {
    return `$${(price / 1000000).toFixed(1)}M`;
  }
  return `$${Math.round(price / 1000)}K`;
};

// Create Zillow-style price marker
const createPriceMarker = (price: number, isSelected: boolean): L.DivIcon => {
  const formattedPrice = formatPriceLabel(price);

  return L.divIcon({
    className: 'price-marker',
    html: `<div class="price-pin ${isSelected ? 'selected' : ''}">${formattedPrice}</div>`,
    iconSize: [70, 32],
    iconAnchor: [35, 32],
    popupAnchor: [0, -32],
  });
};

interface PropertyMapProps {
  properties: InvestmentProperty[];
  clusters?: PropertyCluster[]; // Server-side clusters for zoomed-out view
  selectedProperty: InvestmentProperty | null;
  onPropertySelect: (property: InvestmentProperty) => void;
  loanType: 'FHA' | 'CONV' | 'DSCR';
  onBoundsChange?: (bounds: MapBounds, zoom: number) => void;
  filterByMap?: boolean;
}

// Create cluster marker with count
const createClusterMarker = (count: number, avgPrice: number): L.DivIcon => {
  const priceLabel = formatPriceLabel(avgPrice);
  let sizeClass = 'small';
  if (count > 100) sizeClass = 'large';
  else if (count > 20) sizeClass = 'medium';

  return L.divIcon({
    html: `<div class="server-cluster ${sizeClass}">
      <span class="cluster-count">${count}</span>
      <span class="cluster-price">${priceLabel}</span>
    </div>`,
    className: 'server-cluster-marker',
    iconSize: L.point(60, 50, true),
    iconAnchor: [30, 50],
  });
};

// Component to fit bounds when properties change
function FitBounds({ properties, disabled }: { properties: InvestmentProperty[]; disabled?: boolean }) {
  const map = useMap();
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Completely skip when disabled (user is filtering by map view)
    if (disabled) return;
    // Skip if already initialized (only fit bounds once)
    if (hasInitialized.current) return;
    if (properties.length === 0) return;

    // Get bounds from properties with coordinates
    const propsWithCoords = properties.filter(p => p.latitude && p.longitude);
    if (propsWithCoords.length === 0) return;

    const bounds = L.latLngBounds(
      propsWithCoords.map(p => [p.latitude!, p.longitude!] as [number, number])
    );

    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    hasInitialized.current = true;
  }, [properties, map, disabled]);

  return null;
}

// Component to highlight selected property
function SelectedPropertyHandler({
  selectedProperty,
}: {
  selectedProperty: InvestmentProperty | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (selectedProperty?.latitude && selectedProperty?.longitude) {
      map.flyTo([selectedProperty.latitude, selectedProperty.longitude], 14, {
        duration: 0.5,
      });
    }
  }, [selectedProperty, map]);

  return null;
}

// Component to track map bounds and zoom changes
function BoundsHandler({
  onBoundsChange,
}: {
  onBoundsChange?: (bounds: MapBounds, zoom: number) => void;
}) {
  const map = useMap();

  useEffect(() => {
    if (!onBoundsChange) return;

    const handleMoveEnd = () => {
      const bounds = map.getBounds();
      const zoom = map.getZoom();
      onBoundsChange({
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      }, zoom);
    };

    // Fire initially to set bounds and zoom
    handleMoveEnd();

    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleMoveEnd);
    return () => {
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleMoveEnd);
    };
  }, [map, onBoundsChange]);

  return null;
}

// Get property image URL with R2 preference
const getImageUrl = (property: InvestmentProperty): string | undefined => {
  if (property.r2ImageKey) {
    return `https://property-api.aelieve.workers.dev/api/images/${property.r2ImageKey}`;
  }
  return property.mainImageUrl;
};

export function PropertyMap({
  properties,
  clusters,
  selectedProperty,
  onPropertySelect,
  loanType,
  onBoundsChange,
  filterByMap,
}: PropertyMapProps) {
  const mapRef = useRef<L.Map | null>(null);

  // Get cashflow based on loan type
  const getCashflow = (property: InvestmentProperty): number => {
    switch (loanType) {
      case 'FHA':
        return property.fhaMonthlyCashflow;
      case 'CONV':
        return property.convMonthlyCashflow;
      case 'DSCR':
        return property.dscrMonthlyCashflow;
    }
  };

  // US center coordinates
  const defaultCenter: [number, number] = [39.8283, -98.5795];
  const defaultZoom = 4;

  // Filter properties with valid coordinates
  const mappableProperties = useMemo(() =>
    properties.filter(p => p.latitude && p.longitude),
    [properties]
  );

  // Use server clusters if provided, otherwise use individual properties
  const hasServerClusters = clusters && clusters.length > 0;

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  return (
    <div className="property-map-container">
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        className="property-map"
        ref={mapRef}
      >
        {/* CartoDB Positron (muted) tiles for Zillow-style clean look */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />

        <FitBounds properties={mappableProperties} disabled={filterByMap} />
        <SelectedPropertyHandler selectedProperty={selectedProperty} />
        <BoundsHandler onBoundsChange={onBoundsChange} />

        {/* Server-side clusters (zoomed out) */}
        {hasServerClusters && clusters.map((cluster, index) => (
          <Marker
            key={`cluster-${index}`}
            position={[cluster.lat, cluster.lng]}
            icon={createClusterMarker(cluster.count, cluster.avgPrice)}
          >
            <Popup className="cluster-popup">
              <div className="cluster-info">
                <div className="cluster-stat"><strong>{cluster.count}</strong> properties</div>
                <div className="cluster-stat">Avg: {formatCurrency(cluster.avgPrice)}</div>
                <div className="cluster-stat">Score: {cluster.avgScore.toFixed(1)}</div>
                <div className="cluster-range">{formatCurrency(cluster.minPrice)} - {formatCurrency(cluster.maxPrice)}</div>
                <div className="cluster-hint">Zoom in to see individual properties</div>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Individual properties with client-side clustering (zoomed in) */}
        {!hasServerClusters && (
          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={60}
            spiderfyOnMaxZoom={false}
            showCoverageOnHover={false}
            zoomToBoundsOnClick
            animate={false}
            iconCreateFunction={(clusterObj: { getChildCount: () => number }) => {
              const count = clusterObj.getChildCount();
              let sizeClass = 'small';
              if (count > 100) sizeClass = 'large';
              else if (count > 20) sizeClass = 'medium';

              return L.divIcon({
                html: `<div class="map-cluster ${sizeClass}">${count}</div>`,
                className: 'cluster-marker',
                iconSize: L.point(44, 44, true),
              });
            }}
          >
            {mappableProperties.map((property) => (
              <Marker
                key={property.id}
                position={[Number(property.latitude), Number(property.longitude)]}
                icon={createPriceMarker(property.listPrice, selectedProperty?.id === property.id)}
                eventHandlers={{
                  click: () => onPropertySelect(property),
                }}
              >
                <Popup className="zillow-popup" maxWidth={300}>
                  <div className="popup-card">
                    {getImageUrl(property) && (
                      <div className="popup-image">
                        <img src={getImageUrl(property)} alt="" />
                        <div className="popup-score-badge">
                          <span className="score-value">{Number(property.investmentScore).toFixed(1)}</span>
                        </div>
                      </div>
                    )}
                    <div className="popup-content">
                      <div className="popup-price">{formatCurrency(property.listPrice)}</div>
                      <div className="popup-details">
                        {property.bedrooms} bd &middot; {property.bathrooms} ba &middot; {property.sqft?.toLocaleString()} sqft
                      </div>
                      <div className="popup-address">{property.fullAddress}</div>
                      <div className="popup-metrics">
                        <span className="metric">Rent: {formatCurrency(property.monthlyRent)}/mo</span>
                        <span className="metric">Cap: {property.capRatePct != null ? Number(property.capRatePct).toFixed(1) : 'N/A'}%</span>
                      </div>
                      <div className={`popup-cashflow ${getCashflow(property) >= 0 ? 'positive' : 'negative'}`}>
                        {loanType} Cashflow: {formatCurrency(getCashflow(property))}/mo
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MarkerClusterGroup>
        )}
      </MapContainer>

      {!hasServerClusters && mappableProperties.length === 0 && properties.length > 0 && (
        <div className="map-no-coords">
          <p>No properties with coordinates to display</p>
        </div>
      )}

      {!hasServerClusters && properties.length === 0 && !clusters?.length && (
        <div className="map-no-properties">
          <p>No properties match your filters</p>
        </div>
      )}
    </div>
  );
}
